import { ApiError } from '../api/client';

/** Same 403/404/400/fallback idiom `invoice-api-errors.ts`'s `genericApiErrorMessage` already applies. */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'No encontramos ese cliente dentro de tu alcance.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/** Codes from `apps/api` `PaymentExceptionFilter`. */
export function mapPaymentError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  /**
   * `payment-receipts/spec.md` → "Idempotencia ante reintento de red": the
   * same `idempotencyKey` with the *same* body replays with `200`, never
   * this error. `409 IDEMPOTENCY_KEY_REUSED` only happens when the key was
   * reused with different data (`isSamePaymentRequest` failed).
   */
  if (error.status === 409 && code === 'IDEMPOTENCY_KEY_REUSED') {
    return 'Ya existe un recibo registrado con esta clave, con otros datos. Recargá la página e intentá de nuevo.';
  }

  if (error.status === 422 && code === 'PAYMENT_AMOUNT_NOT_POSITIVE') {
    return 'El monto recibido debe ser mayor a cero.';
  }

  if (error.status === 422 && code === 'WITHHOLDING_AMOUNT_NOT_POSITIVE') {
    return 'Cada línea de retención debe tener un monto mayor a cero.';
  }

  if (error.status === 422 && code === 'ALLOCATION_AMOUNT_NOT_POSITIVE') {
    return 'Cada línea de imputación debe tener un monto mayor a cero.';
  }

  if (error.status === 422 && code === 'ALLOCATION_EXCEEDS_PAYMENT_APPLICABLE') {
    return 'El total imputado supera el monto disponible del recibo.';
  }

  if (error.status === 422 && code === 'ALLOCATION_EXCEEDS_INVOICE_BALANCE') {
    return 'El monto imputado a una factura supera su saldo pendiente.';
  }

  if (error.status === 422 && code === 'INVOICE_CUSTOMER_MISMATCH') {
    return 'Una de las facturas no pertenece al cliente de este recibo.';
  }

  if (error.status === 409 && code === 'INVOICE_NOT_ALLOCATABLE') {
    return 'Una de las facturas ya no admite nuevas imputaciones.';
  }

  if (error.status === 409 && code === 'PAYMENT_VOIDED') {
    return 'Este recibo está anulado y no admite nuevas imputaciones.';
  }

  if (error.status === 409 && code === 'PAYMENT_ALREADY_VOIDED') {
    return 'Este recibo ya estaba anulado.';
  }

  if (error.status === 422 && code === 'VOID_REASON_REQUIRED') {
    return 'El motivo de la anulación es obligatorio.';
  }

  return genericApiErrorMessage(error);
}
